import {
  AnyObject,
  defaultStageValidate,
  EncounterSections,
  FieldMap,
  FieldMapValue,
  FormValidate,
  getFieldFromPath,
  getFieldPaths,
  getFieldPathText,
  getSectionValidationField,
  isField,
  ValidationErrors,
} from '@hb/shared'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useDeferredValue,
  useMemo,
} from 'react'
import { useForm, useFormState } from 'react-final-form'

interface EncounterErrorsContextValue {
  errors: ValidationErrors | undefined
  sectionsField: FieldMap
  firstError: { text: string; path: string } | null
  /** True when getEncounterValidate finds at least one tier-1 ('Required') error. */
  tier1ErrorExists: boolean
  /** §M.7: All Tier 1 errors as a flat list, in visual field order, for the counter banner. */
  allTier1Errors: Array<{ text: string; path: string }>
  /** §M.7: Focus + briefly highlight a field by its Final Form path. */
  scrollToAndHighlight: (path: string) => void
}

const EncounterErrorsContext = createContext<EncounterErrorsContextValue>({
  errors: undefined,
  sectionsField: { name: 'Encounter Data', children: {} },
  firstError: null,
  tier1ErrorExists: false,
  allTier1Errors: [],
  scrollToAndHighlight: () => undefined,
})

export const useEncounterErrors = () => useContext(EncounterErrorsContext)

// Read a leaf value out of a nested errors object by dot path (e.g. 'data.soapNote.subjective').
const readLeaf = (obj: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((cur, key) => {
    if (cur == null || typeof cur !== 'object') return undefined
    return (cur as AnyObject)[key]
  }, obj)

// §M.7: Some Tier-1 paths the tier validator emits have no entry in sectionsField — they're
// rendered by bespoke useField inputs (gaConfirmed, gbsStatusAcknowledged) or live under a
// different FieldMap key than their data path (timeWorkLog.*). For those we humanize the last
// path segment so the banner row is never blank. Overrides cover awkward acronyms.
const HUMANIZE_OVERRIDES: Record<string, string> = {
  icd10Codes: 'ICD-10 Codes',
  gaConfirmed: 'GA Confirmed',
  gbsStatusAcknowledged: 'GBS Status Acknowledged',
  sonographerSignOff: 'Sonographer Sign-Off',
  fetalHeartTones: 'Fetal Heart Tones',
  fetalPosition: 'Fetal Position',
  preVisitContactMinutes: 'Pre-Visit Contact Time',
  substanceUseCounselingMinutes: 'Substance Use Counseling Time',
}

const humanizeKey = (key: string): string => {
  if (HUMANIZE_OVERRIDES[key]) return HUMANIZE_OVERRIDES[key]
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._]/g, ' ')
    .trim()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key
}

const buildSectionsFieldMap = (sections: EncounterSections): FieldMap => ({
  name: 'Encounter Data',
  children: sections.reduce<FieldMap['children']>((acc, section) => {
    const sectionField = getSectionValidationField[section.type](section as never)
    if (!sectionField) return acc
    return { ...acc, ...sectionField.children }
  }, {}),
})

export const EncounterErrorsProvider = ({
  validate,
  sections,
  children,
}: PropsWithChildren<{
  sections: EncounterSections
  validate: FormValidate<AnyObject> | undefined
}>) => {
  const form = useForm()
  const { values: liveValues } = useFormState({ subscription: { values: true } })

  // Validating the whole encounter field map on every keystroke is the dominant typing-lag
  // source. None of the derived error state (Next Step gating, the live error counter, the
  // §M.7 highlight list) needs to be frame-accurate while the user is mid-word, so we run all
  // of it against the deferred values. React keeps keystrokes responsive and recomputes the
  // (expensive) validation at lower priority once typing settles.
  const values = useDeferredValue(liveValues)

  const sectionsField = useMemo(() => buildSectionsFieldMap(sections), [sections])

  // Always derive displayErrors from defaultStageValidate so the path structure
  // matches sectionsField (no 'data' prefix). getEncounterValidate paths are
  // prefixed with 'data' and would fail getFieldFromPath lookups.
  const displayErrors = useMemo(
    () => defaultStageValidate(sectionsField)(values?.data as FieldMapValue),
    [sectionsField, values?.data],
  )

  // Run the form-level validate at most once per value change — it walks the entire
  // encounter field map, so calling it separately for `errors` and `tier1ErrorExists`
  // (as before) doubled that cost on every keystroke and was a major typing-lag source.
  const validateErrors = useMemo(
    () => (validate ? validate(values as AnyObject) : undefined),
    [validate, values],
  )

  // errors drives the Next Step button. Call validate with the full values object
  // (not values.data) — validate's paths are prefixed with 'data.' so it needs the
  // full form values to resolve them correctly.
  const errors = validate ? validateErrors : displayErrors

  // tier1ErrorExists: true when validate finds any tier-1 ('Required') error.
  // Used by the Authenticate button to gate sign-off even for fields (e.g. icd10Codes)
  // that are not in sectionsField and would be invisible to firstError.
  const tier1ErrorExists = useMemo(() => {
    if (!validate || !values || !validateErrors) return false
    return getFieldPaths(validateErrors).some(
      rawPath => readLeaf(validateErrors, rawPath) === 'Required',
    )
  }, [validate, values, validateErrors])

  // §M.7: The Tier-1 ('Required') errors, in section order, sourced from the SAME tier validator
  // that gates authentication (validateErrors) so the banner can never disagree with the auth gate.
  // validateErrors paths are 'data.'-prefixed; sectionsField has no 'data' level, so we strip it for
  // label lookup. Labels resolve through sectionsField when possible, else fall back to a humanized
  // key (some Tier-1 paths — gaConfirmed, timeWorkLog.*, etc. — aren't in sectionsField). getFieldPathText
  // throws on an unresolved segment, so we must resolve+isField-check before calling it.
  // When validate is undefined (brief window before useEncounterValidate sets it), fall back to
  // displayErrors (intrinsic FieldMap validation) in sectionsField order.
  const tier1Errors = useMemo(() => {
    const result: Array<{ text: string; path: string }> = []
    const resolveLabel = (stripped: string): string => {
      const field = getFieldFromPath(sectionsField, stripped)
      return field && isField(field)
        ? getFieldPathText(sectionsField, stripped)
        : humanizeKey(stripped.split('.').pop() ?? stripped)
    }
    if (validate) {
      if (!validateErrors) return result
      for (const rawPath of getFieldPaths(validateErrors)) {
        if (readLeaf(validateErrors, rawPath) !== 'Required') continue
        result.push({ text: resolveLabel(rawPath.replace(/^data\./, '')), path: rawPath })
      }
      return result
    }
    for (const errorPath of getFieldPaths(displayErrors)) {
      const field = getFieldFromPath(sectionsField, errorPath)
      if (!field || !isField(field)) continue
      result.push({ text: getFieldPathText(sectionsField, errorPath), path: `data.${errorPath}` })
    }
    return result
  }, [validate, validateErrors, displayErrors, sectionsField])

  // firstError: first missing required field in section order (drives Next Step gating display).
  const firstError = useMemo(() => {
    if (!values?.date) return { text: 'Date is required', path: 'date' }
    return tier1Errors[0] ?? null
  }, [tier1Errors, values?.date])

  // §M.7: All Tier 1 errors for the counter banner and popover list — exam date first (if missing),
  // then every Tier-1 error.
  const allTier1Errors = useMemo(() => {
    const result: Array<{ text: string; path: string }> = []
    if (!values?.date) {
      result.push({ text: 'Exam Date', path: 'date' })
    }
    result.push(...tier1Errors)
    return result
  }, [tier1Errors, values?.date])

  // §M.7: Scroll the errored field's section into view, move browser focus to the
  // input, then briefly flash an orange ring around its container.
  // NOTE: react-final-form's form.focus() only flips the field's internal `active`
  // flag — it does not scroll or move DOM focus, so document.activeElement is not
  // the field. We locate the DOM directly instead. Section inputs tag their outer
  // container with data-child-name; the raw input carries the final-form `name`
  // (text) or id={name} (date/select).
  const scrollToAndHighlight = useCallback(
    (path: string) => {
      // Defer so the triggering popover has closed and any conditional section has settled.
      setTimeout(() => {
        // The actual input element, if it renders the final-form name onto the DOM.
        const input = document.querySelector<HTMLElement>(`[name="${path}"]`)
        // Highlight target: an explicitly tagged section container for this exact path,
        // else the input's nearest tagged container, else a field that renders id={name},
        // else the raw input itself.
        const el: HTMLElement | null =
          document.querySelector<HTMLElement>(`[data-child-name="${path}"]`) ??
          input?.closest<HTMLElement>('[data-child-name]') ??
          document.getElementById(path) ??
          input
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Mirror final-form active state for components that react to it, then move
        // real browser focus to the input without re-triggering the scroll we just did.
        const focusTarget = el ?? input
        if (typeof focusTarget.focus === 'function') focusTarget.focus({ preventScroll: true })
        const prevShadow = el.style.boxShadow
        const prevTransition = el.style.transition
        el.style.transition = 'box-shadow 0.3s ease'
        el.style.boxShadow = '0 0 3px 3px #ED8936'
        setTimeout(() => {
          form.focus(path)
        }, 300)
        setTimeout(() => {
          el.style.boxShadow = prevShadow
          el.style.transition = prevTransition
        }, 2000)
      }, 50)
    },
    [form],
  )

  const contextValue = useMemo<EncounterErrorsContextValue>(
    () => ({
      errors,
      sectionsField,
      firstError,
      tier1ErrorExists,
      allTier1Errors,
      scrollToAndHighlight,
    }),
    [errors, sectionsField, firstError, tier1ErrorExists, allTier1Errors, scrollToAndHighlight],
  )

  return (
    <EncounterErrorsContext.Provider value={contextValue}>
      {children}
    </EncounterErrorsContext.Provider>
  )
}
